import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { useFiltersInitialDates } from '@hooks/customHooks/useFiltersInitialDates';
import { FiltersForm } from '@shared/types/forms';
import { decodeSearchParams } from './utils';

export interface CaseDashboardProps {
  filters: FiltersForm;
  unsavedChanges: boolean;
  setUnsavedChanges: (value: boolean) => void;
  totalClaims?: number;
  setTotalClaimsCount?: (count: number) => void;
  handleClearFilterDates: () => void;
}

export const CaseDashboardContext = createContext<CaseDashboardProps>({
  filters: {},
  totalClaims: undefined,
  unsavedChanges: false,
  setTotalClaimsCount: () => undefined,
  handleClearFilterDates: () => undefined,
  setUnsavedChanges: () => undefined,
});

export function CaseDashboardProvider({ children }: PropsWithChildren) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: FiltersForm = decodeSearchParams(searchParams);

  const { isDefaultDateRange } = useFiltersInitialDates();
  const [totalClaims, setTotalClaims] = useState<number>(0);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const { isLoading: isUserOrgCaseDataLoading } = useUserOrgCase();

  useEffect(() => {
    if (isDefaultDateRange) {
      setSearchParams((prev) => {
        prev.delete('startDate');
        prev.delete('endDate');
        return prev;
      });
    }
  }, [filters.startDate, filters.endDate, setSearchParams, isDefaultDateRange]);

  const handleClearFilterDates = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('startDate');
      prev.delete('endDate');
      return prev;
    });
  }, [setSearchParams]);

  const setTotalClaimsCount = useCallback((count: number) => {
    setTotalClaims(count);
  }, []);

  const caseDashboardValues = useMemo(
    () => ({
      filters,
      totalClaims,
      setTotalClaimsCount,
      handleClearFilterDates,
      setUnsavedChanges,
      unsavedChanges,
    }),
    [
      filters,
      handleClearFilterDates,
      totalClaims,
      setTotalClaimsCount,
      unsavedChanges,
      setUnsavedChanges,
    ],
  );

  if (isUserOrgCaseDataLoading) {
    return null;
  }

  return (
    <CaseDashboardContext.Provider value={caseDashboardValues}>
      {children}
    </CaseDashboardContext.Provider>
  );
}

export const useCaseDashboardContext = () => {
  const context = React.useContext(CaseDashboardContext);

  if (!context) {
    throw new Error(
      'useCaseDashboardContext must be used within a CaseDashboardProvider',
    );
  }

  return context;
};
